/**
 * FieldTooltip Component
 *
 * Wraps a child element with a tooltip showing field-level help
 */

import React, { useState, useCallback } from 'react';
import { HelpCircle, Calculator, Lightbulb, ExternalLink } from 'lucide-react';
import { useFieldHelp } from '../hooks/useFieldHelp';
import { cn } from '../../../lib/utils';

interface FieldTooltipProps {
  fieldKey: string;
  children: React.ReactNode;
  showIcon?: boolean;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export const FieldTooltip: React.FC<FieldTooltipProps> = ({
  fieldKey,
  children,
  showIcon = false,
  position = 'top',
}) => {
  const { fieldHelp, isLoading, hasHelp } = useFieldHelp(fieldKey);
  const [isVisible, setIsVisible] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setIsVisible(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsVisible(false);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(prev => !prev);
  }, []);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-900 dark:border-t-gray-700',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 dark:border-b-gray-700',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-900 dark:border-l-gray-700',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-gray-900 dark:border-r-gray-700',
  };

  if (!hasHelp && !isLoading) {
    return <>{children}</>;
  }

  return (
    <div
      className="relative inline-flex items-center gap-1"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {showIcon && hasHelp && (
        <button
          onClick={handleClick}
          className="inline-flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          aria-label="Show help"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      )}

      {isVisible && fieldHelp && (
        <div
          className={cn(
            'absolute z-50 w-72 p-3 rounded-lg shadow-lg',
            'bg-gray-900 dark:bg-gray-700 text-white',
            'text-sm',
            positionClasses[position]
          )}
          role="tooltip"
        >
          {/* Arrow */}
          <div
            className={cn(
              'absolute w-0 h-0 border-4 border-transparent',
              arrowClasses[position]
            )}
          />

          {/* Label */}
          <div className="font-semibold text-white mb-1">
            {fieldHelp.label}
          </div>

          {/* Description */}
          <p className="text-gray-200 text-xs mb-2">
            {fieldHelp.description}
          </p>

          {/* Formula */}
          {fieldHelp.formula && (
            <div className="flex items-start gap-2 mb-2 p-2 bg-gray-800 dark:bg-gray-600 rounded">
              <Calculator className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs text-gray-400 mb-0.5">Formula</div>
                <code className="text-xs text-blue-300 font-mono">{fieldHelp.formula}</code>
              </div>
            </div>
          )}

          {/* Example */}
          {fieldHelp.example && (
            <div className="flex items-start gap-2 p-2 bg-gray-800 dark:bg-gray-600 rounded">
              <Lightbulb className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs text-gray-400 mb-0.5">Example</div>
                <div className="text-xs text-gray-200">{fieldHelp.example}</div>
              </div>
            </div>
          )}

          {/* Learn more link */}
          {fieldHelp.learn_more_url && (
            <a
              href={fieldHelp.learn_more_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 mt-2 text-xs text-blue-400 hover:text-blue-300"
            >
              Learn more <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
};
